import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useStateIfMounted } from 'use-state-if-mounted';
import { Typography, CircularProgress, Box, styled, useTheme } from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';

import { NETWORK, NETWORK_DEFAULT } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';
import { getFullMediaUrl } from 'utils/image';
import { shorten } from 'utils/helper';
import { CollectionsContext } from 'context/CollectionsContext';

import { CurrencyIcon } from 'components/Currency';
//import { ApprovedIcon} from 'components/ImageComponent';
import { FlexBox } from 'components/StyledComponents';
import Blockie from 'components/Blockie';

/********************  Styled Components  ********************/
const ResultContainer = styled('div')(({ theme, fullHeight }) => ({
  backgroundColor: theme.palette.background.default,
  width: '100%',
  minWidth: '482px',
  maxHeight: 'calc(var(--vh, 1vh) * 100 - 79px)',
  overflowY: 'auto',
  alignItems: 'center',
  className: 'search-results-container',

  '@media (max-width: 1440px)': {
    left: '2px',
  },

  '@media (max-width: 1280px)': {
    width: '100%',
    minWidth: '508px',
  },

  '@media (max-width: 940px)': {
    minWidth: '350px',
  },

  '@media(min-width: 700px)': {
    position: 'absolute',
    right: 0,
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    maxHeight: 'calc(var(--vh, 1vh) * 100 - 144px)',
    overflowY: 'auto',
    alignItems: 'center',
    boxShadow: '0px 1px 8px 3px #00000033',
    '&:hover': {
      display: 'block',
    },
  },

  '@media(max-width: 700px)': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: fullHeight ? 'center' : 'flex-start',
  },

  '& > div': {
    width: '100%',
  },
}));

const ResultRow = styled('div')(({ theme }) => ({
  display: 'flex',
  cursor: 'pointer',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.background.default,
  width: '100%',
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.border.separator}`,
  height: 60,
}));
const SearchResultContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
}));
const SearchResultTextContainer = styled('div')(() => ({
  display: 'flex',
  padding: '0 8px',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const TitleContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: 0,
  height: '24px',
}));

const SearchResultImageContainer = styled('div')(() => ({
  height: 32,
  width: 32,
  overflow: 'hidden',
  borderRadius: '8px',
}));

const SearchImg = styled('img')(({ profile }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: profile ? '50%' : '8px',
}));

const SearchResultTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  fontFeatureSettings: "'cv11' on, 'cv09' on, 'cv03' on, 'cv04' on, 'cv02' on, 'ss01' on",
  color: theme.palette.text.main,
}));

const SearchResultSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 500,
  lineHeight: '15px',
  letterSpacing: '0.4px',
  color: theme.palette.text.main,
}));

const NoSearchResultText = styled(Typography)(({ theme, isError }) => ({
  display: 'flex',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24x',
  letterSpacing: '0.15px',
  color: isError ? theme.palette.error.main : theme.palette.text.secondary,
  padding: 16,
}));

const LoadingBox = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  placeItems: 'center',
  padding: 18,
}));

const SubHeaderContainer = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '10px 16px',
  gap: '8px',
  width: '100%',
  height: '40px',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.border.separator}`,
}));

const SubHeaderItem = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  padding: 0,
  gap: '16px',
  width: '100%',
  height: '20px',
}));

const SubHeaderItemText = styled(Typography)(({ theme }) => ({
  height: '20px',
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  fontFeatureSettings: "'cv11' on, 'cv09' on, 'cv03' on, 'cv04' on, 'cv02' on, 'ss01' on",
  color: theme.palette.text.secondary,
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '10px 16px',
  gap: '8px',
  width: '100%',
  height: '40px',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.border.separator}`,
}));

const CustomMoreButton = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '4px 8px 4px 16px',
  gap: '5px',
  boxSizing: 'border-box',
}));

const SearchItem = ({ item, setSearchText, type, onClose }) => {
  const theme = useTheme();
  const history = useHistory();
  const { storedChain } = useWalletContext();

  const currency = NETWORK[storedChain] ?? NETWORK_DEFAULT;
  const itemProps = {
    url: '',
    name: '',
    subtitle: '',
    image: '',
    address: item?.address,
  };

  if (type === 'account') {
    Object.assign(itemProps, {
      url: `/portfolio/${item.address}`,
      name: item.username,
      subtitle: item.bio,
      image: item.avatar,
    });
  } else if (type === 'collection') {
    Object.assign(itemProps, {
      url: `/collections/${item?.address}`,
      name: item.name,
      subtitle: item.symbol,
      image: getFullMediaUrl(item.contractThumbnailPathName),
    });
  } else if (type === 'item') {
    Object.assign(itemProps, {
      url: `/asset/${item?.contractAddress}/${item?.tokenId}`,
      name: item.name,
      subtitle: item.symbol,
      image: getFullMediaUrl(item.thumbnailPathName, item.rawFileUrl),
    });
  }

  const { url, name, subtitle, image, address } = itemProps;

  return (
    <ResultRow
      onClick={() => {
        if (url) {
          setSearchText('');
          onClose();
          history.push(url);
        }
      }}
    >
      <SearchResultContent>
        <SearchResultImageContainer>
          {Boolean(image) ? <SearchImg src={image} /> : <Blockie avatar={address} />}
        </SearchResultImageContainer>
        <SearchResultTextContainer>
          <TitleContainer>
            <SearchResultTitle>{Boolean(name) ? name : shorten(address, 5)}</SearchResultTitle>
            {/*<ApprovedIcon width={'16px'} height={'16px'} />*/}
          </TitleContainer>
          <SearchResultSubtitle>{subtitle}</SearchResultSubtitle>
        </SearchResultTextContainer>
      </SearchResultContent>
      <CurrencyIcon
        props={{
          fill: theme.palette.icon.secondary,
        }}
        currency={currency.symbol}
        type={'symbol'}
      />
    </ResultRow>
  );
};

/************************* Main Component  **************************/
const SearchResultContainer = ({
  searchResult,
  isLoading,
  setSearchText,
  isError,
  onClose,
  isInputFocused,
}) => {
  const [showCollection, setShowCollection] = useStateIfMounted(false);
  const [showAccount, setShowAccount] = useStateIfMounted(false);
  const [showItems, setShowItems] = useStateIfMounted(false);
  const [showRecent, setShowRecent] = useStateIfMounted(false);

  const { state: collectionsState } = useContext(CollectionsContext);
  const { collections, accounts, items } = searchResult;
  const { recentlyVisitedCollections } = collectionsState;

  const showRecentlyVisited =
    recentlyVisitedCollections?.length > 0 &&
    ((isInputFocused && (!collections?.length || !searchResult?.collections)) ||
      (!collections?.length && !accounts?.length && !items?.length));

  const renderSection = (title, data, expanded, toggleExpanded, type) => (
    <Box>
      <SubHeaderContainer>
        <SubHeaderItem>
          <SubHeaderItemText>{title}</SubHeaderItemText>
        </SubHeaderItem>
      </SubHeaderContainer>

      {(expanded ? data : data?.slice(0, 3))?.map((item) => (
        <SearchItem
          key={item?.id}
          item={item}
          setSearchText={setSearchText}
          type={type}
          onClose={onClose}
        />
      ))}

      {data?.length > 3 && (
        <ButtonContainer>
          <CustomMoreButton onClick={toggleExpanded}>
            <FlexBox>
              {expanded ? 'Less' : 'More'}
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </FlexBox>
          </CustomMoreButton>
        </ButtonContainer>
      )}
    </Box>
  );

  return (
    <ResultContainer>
      {isLoading && (
        <LoadingBox>
          <CircularProgress size={24} />
        </LoadingBox>
      )}
      {isError && <NoSearchResultText isError>{'Error occurred'}</NoSearchResultText>}
      {!isLoading && !isError && (
        <>
          {showRecentlyVisited &&
            renderSection(
              'Recently Visited',
              recentlyVisitedCollections,
              showRecent,
              () => setShowRecent(!showRecent),
              'collection',
            )}

          {collections?.length > 0 &&
            renderSection(
              'Collections',
              collections,
              showCollection,
              () => setShowCollection(!showCollection),
              'collection',
            )}

          {accounts?.length > 0 &&
            renderSection(
              'Accounts',
              accounts,
              showAccount,
              () => setShowAccount(!showAccount),
              'account',
            )}

          {items?.length > 0 &&
            renderSection('Items', items, showItems, () => setShowItems(!showItems), 'item')}
        </>
      )}
    </ResultContainer>
  );
};

export default SearchResultContainer;
